import type { CheckoutMethod, CheckoutSession, GAEventData } from '@checkout/core'

function itemVariant(session: CheckoutSession): string | undefined {
  const period = session.target.billing_period?.trim().toLowerCase()
  if (period === 'monthly') return 'monthly'
  if (period === 'annual' || period === 'yearly') return 'yearly'
  if (period === 'lifetime') return 'lifetime'
  return undefined
}

function paymentType(method: CheckoutMethod): string {
  if (method.provider === 'paypal') return 'paypal'
  if (method.provider === 'stripe' && method.pm_id === 'card') return 'stripe_card'
  return `${method.provider}_${method.pm_id}`
}

function ecommerce(session: CheckoutSession, method: CheckoutMethod) {
  const breakdown = method.amount_breakdown ?? session.pricing_input.amount_breakdown
  const quantity = breakdown.quantity
  const price = Number(breakdown.unit_amount)
  const value = Number(breakdown.total_amount)
  const currency = (method.currency || session.pricing_input.currency).trim().toUpperCase()
  const category = session.target.item_category
  if (
    !Number.isFinite(price) ||
    price < 0 ||
    !Number.isFinite(value) ||
    value < 0 ||
    !Number.isSafeInteger(quantity) ||
    quantity <= 0 ||
    !/^[A-Z]{3}$/.test(currency) ||
    !category
  )
    return null
  const variant = itemVariant(session)
  return {
    currency,
    value,
    items: [
      {
        item_id: session.target.reference,
        item_name: session.target.name,
        item_category: category,
        ...(variant ? { item_variant: variant } : {}),
        price,
        quantity,
      },
    ],
  }
}

export function createAddPaymentInfoEvent(
  session: CheckoutSession,
  method: CheckoutMethod
): GAEventData | null {
  const ecommerceData = ecommerce(session, method)
  if (!ecommerceData) return null
  const methodType = paymentType(method)
  return {
    event: 'add_payment_info',
    payment_type: methodType,
    ecommerce: {
      ...ecommerceData,
      payment_type: methodType,
    },
  }
}

export function createPurchaseFailedEvent(
  method: CheckoutMethod,
  errorCode: 'provider_failed' | 'execution_failed'
): GAEventData {
  return {
    event: 'purchase_failed',
    payment_type: paymentType(method),
    error_code: errorCode,
  }
}
